// Jul-2026 — Bank Reconciliation Exception Approval (frontend).
// Controlled acknowledgement workflow for bank transactions with NO
// corresponding ERP accounting entry. Zero accounting impact — approval
// marks the txn as an "Approved Exception – Book Entry Pending", NOT as
// reconciled, until a real ERP voucher is later created and linked.
import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldAlert, X, Paperclip, Loader2, Link2, Undo2, Ban, Download, Search,
} from "lucide-react";

export const EXC_CHIP = {
  Pending:  { label: "Exception Approval Pending",              cls: "bg-amber-100 text-amber-800 border-amber-300" },
  Approved: { label: "Approved Exception · Book Entry Pending", cls: "bg-purple-100 text-purple-800 border-purple-300" },
  Linked:   { label: "ERP Reconciled (Exception Linked)",       cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

const LINK_MODULES = [
  ["journal_voucher", "Journal Voucher"],
  ["payment_receipt", "Payment Receipt"],
  ["receipt_voucher", "Receipt Voucher"],
  ["expense_voucher", "Expense Voucher"],
  ["general_payment", "General Payment"],
  ["bill_payment_voucher", "Bill Payment Voucher"],
  ["batch_payment", "Batch Payment"],
  ["sales_invoice", "Sales Invoice"],
];

function fmtDT(s) { return s ? s.slice(0, 19).replace("T", " ") : "—"; }

// ─────────── Segregated summary strip (spec: never combine without split) ───────────
export function ReconSegregationStrip({ summary }) {
  if (!summary) return null;
  const cards = [
    ["ERP Batch Reconciled", summary.erp_reconciled_total, "emerald", "recon-strip-erp"],
    ["Offset · DR↔CR Reversals", summary.offset_reversals, "sky", "recon-strip-offset"],
    ["Approved Exception · Book Entry Pending", summary.approved_exception_pending_book_entry, "purple", "recon-strip-exception"],
    ["Total Accounted For", summary.total_accounted, "slate", "recon-strip-total"],
    ["Exception Approval Pending", summary.exception_approval_pending, "amber", "recon-strip-pending"],
    ["Unmatched & Unapproved", summary.unmatched, "rose", "recon-strip-unmatched"],
  ];
  const tone = {
    emerald: "border-emerald-300 bg-emerald-50/50",
    sky: "border-sky-300 bg-sky-50/50",
    purple: "border-purple-300 bg-purple-50/50",
    slate: "border-slate-300 bg-white",
    amber: "border-amber-300 bg-amber-50/50",
    rose: "border-rose-300 bg-rose-50/50",
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5" data-testid="recon-segregation-strip">
      {cards.map(([label, b, t, tid]) => (
        <div key={label} className={`border px-3 py-2.5 ${tone[t]}`} data-testid={tid}>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 leading-tight">{label}</div>
          <div className="mt-1 text-[11px] tabular leading-tight">
            <span className="text-rose-700 font-bold">DR {formatINR(b?.DR || 0)}</span>
            <span className="text-slate-300 mx-1">·</span>
            <span className="text-emerald-700 font-bold">CR {formatINR(b?.CR || 0)}</span>
          </div>
          <div className="text-[10px] text-slate-400">{b?.count || 0} txn(s)</div>
        </div>
      ))}
    </div>
  );
}

// ─────────── Raise dialog (accountant action) ───────────
export function ExceptionRequestDialog({ txn, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!reason.trim()) { toast.error("Reason is mandatory"); return; }
    setBusy(true);
    try {
      let attachment = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/bank-recon/exceptions/attachment", fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        attachment = up.data;
      }
      const r = await api.post("/bank-recon/exceptions", {
        txn_id: txn.id, reason: reason.trim(), remarks: remarks.trim(), attachment,
      });
      toast.success(`Exception request ${r.data.request_number} submitted for approval`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not raise exception request");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3" onClick={() => !busy && onClose()}>
      <div className="bg-white border border-slate-200 shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="exc-raise-dialog">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <h3 className="font-display text-base font-bold flex items-center gap-2">
            <ShieldAlert size={16} className="text-purple-600"/> Mark for Exception Approval
          </h3>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-slate-700"><X size={16}/></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-700 leading-relaxed">
            <div><b>{txn.value_date}</b> · <span className={txn.cr_dr === "CR" ? "text-emerald-700 font-bold" : "text-rose-700 font-bold"}>{txn.cr_dr} {formatINR(txn.amount)}</span> · {txn.bank_ledger_name || "—"}</div>
            <div className="truncate text-slate-500" title={txn.description}>{txn.description}</div>
            <div className="text-slate-400 mt-0.5">Ref: {txn.transaction_id || "—"}</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 px-3 py-2 text-[11px] text-purple-900 leading-relaxed">
            This does <b>NOT</b> reconcile the transaction. It raises a request for an independent
            approver. Even after approval it stays "<b>Book Entry Pending</b>" until the actual ERP
            voucher is created and linked. No accounting entry is posted by this workflow.
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Reason <span className="text-rose-600">*</span></div>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} data-testid="exc-reason"
              placeholder="Why does this bank transaction have no ERP entry? (management instruction ref, etc.)"
              className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-purple-500 focus:outline-none"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Remarks</div>
            <textarea value={remarks} onChange={e => setRemarks(e.target.value)} rows={2} data-testid="exc-remarks"
              className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-purple-500 focus:outline-none"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Supporting Document (optional · PDF/PNG/JPG ≤ 2 MB)</div>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="exc-attachment"
              className="w-full text-xs border border-slate-300 px-2 py-1.5 file:mr-2 file:px-3 file:py-1 file:border-0 file:bg-slate-100 file:text-slate-700 file:text-[11px] file:font-bold file:uppercase"/>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-slate-300 text-slate-700">Cancel</button>
          <button onClick={submit} disabled={busy || !reason.trim()} data-testid="exc-raise-submit"
            className="inline-flex items-center gap-1.5 bg-purple-600 text-white px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-purple-700 disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin"/> : <ShieldAlert size={13}/>} Submit for Approval
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────── Link ERP voucher dialog (post-approval book entry) ───────────
function LinkVoucherDialog({ exc, onClose, onDone }) {
  const [module, setModule] = useState("journal_voucher");
  const [number, setNumber] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!number.trim()) { toast.error("Voucher number is required"); return; }
    setBusy(true);
    try {
      await api.post(`/bank-recon/exceptions/${exc.id}/link-voucher`, {
        voucher_module: module, voucher_number: number.trim(), note: note.trim(),
      });
      toast.success(`${exc.request_number} linked — now counted as ERP Reconciled`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Linking failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3" onClick={() => !busy && onClose()}>
      <div className="bg-white border border-slate-200 shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="exc-link-dialog">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <h3 className="font-display text-base font-bold flex items-center gap-2"><Link2 size={16} className="text-emerald-600"/> Link ERP Voucher — {exc.request_number}</h3>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-slate-700"><X size={16}/></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-900 leading-relaxed">
            The accounting entry has now been recorded in the ERP. Linking moves this amount from
            "Approved Exception" to "<b>ERP Reconciled</b>" (never counted in both). The original
            approval trail stays preserved.
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Voucher Type</div>
            <select value={module} onChange={e => setModule(e.target.value)} data-testid="exc-link-module"
              className="w-full text-xs border border-slate-300 px-2 py-1.5 bg-white">
              {LINK_MODULES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Voucher Number <span className="text-rose-600">*</span></div>
            <input value={number} onChange={e => setNumber(e.target.value)} data-testid="exc-link-number"
              placeholder="e.g. RCPL/JVEB/0012/26-27" className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-emerald-500 focus:outline-none"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Note</div>
            <input value={note} onChange={e => setNote(e.target.value)} data-testid="exc-link-note"
              className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-emerald-500 focus:outline-none"/>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-slate-300 text-slate-700">Cancel</button>
          <button onClick={submit} disabled={busy || !number.trim()} data-testid="exc-link-submit"
            className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin"/> : <Link2 size={13}/>} Link &amp; Mark ERP Reconciled
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────── Exceptions register tab ───────────
export function ExceptionsTab({ onChanged, currentUserEmail }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [linking, setLinking] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = () => api.get("/bank-recon/exceptions").then(r => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = rows;
    if (status) out = out.filter(r => r.status === status);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      out = out.filter(r => [r.request_number, r.transaction_id, r.description, r.reason, r.bank_ledger_name, r.raised_by_email]
        .some(v => (v || "").toLowerCase().includes(s)));
    }
    return out;
  }, [rows, status, q]);

  async function cancel(exc) {
    if (!window.confirm(`Cancel exception request ${exc.request_number}? The transaction returns to Unmatched.`)) return;
    try {
      await api.post(`/bank-recon/exceptions/${exc.id}/cancel`, {});
      toast.success("Request cancelled");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  }

  async function reverse(exc) {
    const reason = window.prompt(`Reverse approved exception ${exc.request_number}?\n\nA mandatory reason is required (fully audited). The transaction returns to Unmatched.`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is mandatory to reverse"); return; }
    try {
      await api.post(`/bank-recon/exceptions/${exc.id}/reverse`, { reason: reason.trim() });
      toast.success("Exception reversed");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reversal failed"); }
  }

  async function exportExcel() {
    setDownloading(true);
    try {
      const r = await api.get("/bank-recon/exceptions/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Bank-Recon-Exceptions-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch { toast.error("Export failed"); }
    finally { setDownloading(false); }
  }

  const STATUS_CHIP = {
    Pending:   "bg-amber-100 text-amber-800 border-amber-300",
    Approved:  "bg-purple-100 text-purple-800 border-purple-300",
    Linked:    "bg-emerald-100 text-emerald-800 border-emerald-300",
    Rejected:  "bg-rose-100 text-rose-800 border-rose-300",
    Cancelled: "bg-slate-100 text-slate-600 border-slate-300",
    Reversed:  "bg-slate-100 text-slate-600 border-slate-300",
  };

  return (
    <div data-testid="exc-tab">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} data-testid="exc-search"
            placeholder="Search request no / txn ref / reason…"
            className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 focus:border-purple-500 focus:outline-none bg-white"/>
          {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={13}/></button>}
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="exc-status-filter"
          className="text-xs border border-slate-300 px-2 py-2 bg-white">
          <option value="">All statuses</option>
          <option value="Pending">Exception Approval Pending</option>
          <option value="Approved">Approved · Book Entry Pending</option>
          <option value="Linked">ERP Reconciled (Linked)</option>
          <option value="Rejected">Rejected</option>
          <option value="Cancelled">Cancelled</option>
          <option value="Reversed">Reversed</option>
        </select>
        <button onClick={exportExcel} disabled={downloading} data-testid="exc-export-btn"
          className="inline-flex items-center gap-1.5 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-3 py-2 text-[11px] font-bold uppercase tracking-wider ml-auto disabled:opacity-50">
          <Download size={12}/> {downloading ? "Preparing…" : "Export Excel"}
        </button>
      </div>
      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b-2 border-slate-200">
            <tr>{["Request No", "Txn Date", "Bank / Ref", "Type", "Amount", "Reason", "Status", "Ageing", "Raised By", "Approved By / Remarks", "Doc", "Actions"].map(h =>
              <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-3">{h}</th>
            )}</tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={12} className="py-10 text-center text-slate-400">No exception requests{status || q ? " match your filters" : " yet"}.</td></tr>}
            {filtered.map(x => (
              <tr key={x.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`exc-row-${x.request_number}`}>
                <td className="py-2 px-3 font-mono font-bold text-slate-900 whitespace-nowrap">{x.request_number}</td>
                <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{x.value_date}</td>
                <td className="py-2 px-3 text-slate-600 max-w-[180px]">
                  <div className="truncate" title={x.bank_ledger_name}>{x.bank_ledger_name || "—"}</div>
                  <div className="text-[10px] text-slate-400 truncate" title={x.transaction_id}>{x.transaction_id || "—"}</div>
                </td>
                <td className="py-2 px-3">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 ${x.cr_dr === "DR" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>{x.cr_dr}</span>
                </td>
                <td className="py-2 px-3 text-right tabular font-bold whitespace-nowrap">{formatINR(x.amount)}</td>
                <td className="py-2 px-3 text-slate-600 max-w-[220px] truncate" title={`${x.reason}${x.remarks ? ` — ${x.remarks}` : ""}`}>{x.reason}</td>
                <td className="py-2 px-3 whitespace-nowrap">
                  <span className={`text-[10px] font-bold px-2 py-0.5 border ${STATUS_CHIP[x.status] || ""}`} title={x.display_status}>{x.display_status}</span>
                  {x.linked_voucher_number && <div className="text-[10px] text-emerald-700 mt-0.5">→ {x.linked_voucher_number}</div>}
                </td>
                <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{x.status === "Approved" && x.ageing_days !== undefined ? `${x.ageing_days}d (${x.ageing_bucket})` : "—"}</td>
                <td className="py-2 px-3 text-slate-600 max-w-[140px]">
                  <div className="truncate" title={x.raised_by_email}>{x.raised_by_email}</div>
                  <div className="text-[10px] text-slate-400">{fmtDT(x.raised_at)}</div>
                </td>
                <td className="py-2 px-3 text-slate-600 max-w-[170px]">
                  {x.approved_by ? (
                    <>
                      <div className="truncate" title={x.approved_by}>{x.approved_by}</div>
                      <div className="text-[10px] text-slate-400">{fmtDT(x.approved_at)}</div>
                      {x.approver_remarks && <div className="text-[10px] text-slate-500 truncate" title={x.approver_remarks}>"{x.approver_remarks}"</div>}
                    </>
                  ) : (x.rejected_by ? <span className="text-[10px] text-rose-600" title={x.rejection_reason}>Rejected: {x.rejected_by}</span> : "—")}
                </td>
                <td className="py-2 px-3">
                  {x.attachment?.url
                    ? <a href={`${process.env.REACT_APP_BACKEND_URL}${x.attachment.url}`} target="_blank" rel="noreferrer"
                        className="text-purple-600 hover:text-purple-800" title={x.attachment.filename}><Paperclip size={13}/></a>
                    : <span className="text-slate-300">—</span>}
                </td>
                <td className="py-2 px-3 whitespace-nowrap text-right">
                  {x.status === "Pending" && (
                    <button onClick={() => cancel(x)} data-testid={`exc-cancel-${x.request_number}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-slate-500 hover:text-rose-700">
                      <Ban size={11}/> Cancel
                    </button>
                  )}
                  {x.status === "Approved" && (
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => setLinking(x)} data-testid={`exc-link-${x.request_number}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-emerald-700 hover:text-emerald-900">
                        <Link2 size={11}/> Link Voucher
                      </button>
                      <button onClick={() => reverse(x)} data-testid={`exc-reverse-${x.request_number}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-slate-500 hover:text-rose-700">
                        <Undo2 size={11}/> Reverse
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {linking && <LinkVoucherDialog exc={linking} onClose={() => setLinking(null)}
        onDone={() => { setLinking(null); load(); onChanged?.(); }} />}
    </div>
  );
}
